import React from 'react';
import { Link } from 'react-router-dom';

export const ClinicInfoPanel = ({ title, icon, actions, children }) => (
    <div className="clinic-info-panel card">
        <div className="clinic-info-panel__header">
            <div className="clinic-info-panel__heading">
                <span className="clinic-info-panel__icon">
                    <i className={`bi ${icon}`}></i>
                </span>
                <h6 className="clinic-info-panel__title">{title}</h6>
            </div>
            {actions && <div className="clinic-info-panel__actions">{actions}</div>}
        </div>
        <div className="clinic-info-panel__body">{children}</div>
    </div>
);

export const ClinicInfoDetailItem = ({ label, value, children, className = '' }) => (
    <div className={`clinic-info-detail-item ${className}`.trim()}>
        <span className="clinic-info-detail-item__label">{label}</span>
        <div className="clinic-info-detail-item__value">
            {children || value || '—'}
        </div>
    </div>
);

export const VerificationBadge = ({ isVerified, fieldName }) => (
    <span
        className={`clinic-info-verify-badge ${
            isVerified ? 'clinic-info-verify-badge--verified' : 'clinic-info-verify-badge--pending'
        }`}
        title={isVerified ? `${fieldName} verified` : `${fieldName} not verified`}
    >
        <i className={`bi ${isVerified ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}></i>
        {isVerified ? 'Verified' : 'Pending'}
    </span>
);

export const ClinicStatusCard = ({ isActive }) => (
    <div className={`clinic-info-status-card ${isActive ? 'clinic-info-status-card--active' : 'clinic-info-status-card--inactive'}`}>
        <span className="clinic-info-status-card__icon">
            <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
        </span>
        <div className="clinic-info-status-card__content">
            <span className="clinic-info-status-card__label">Clinic Status</span>
            <strong className="clinic-info-status-card__title">
                {isActive ? 'Active' : 'Inactive'}
            </strong>
            <p className="clinic-info-status-card__text mb-0">
                {isActive
                    ? 'Your EyeconX service is operational.'
                    : 'Your EyeconX service is currently stopped.'}
            </p>
        </div>
    </div>
);

export const ClinicLogoCard = ({ logo, logoDimensions }) => (
    <div className="clinic-info-logo-card card">
        <div className="clinic-info-logo-card__header">
            <div className="clinic-info-panel__heading">
                <span className="clinic-info-panel__icon">
                    <i className="bi bi-image"></i>
                </span>
                <h6 className="clinic-info-panel__title mb-0">Clinic Logo</h6>
            </div>
        </div>
        <div className="clinic-info-logo-card__body">
            {logo ? (
                <>
                    <div className="clinic-info-logo-card__frame">
                        <img src={logo} alt="Clinic Logo" className="clinic-info-logo-card__image" />
                    </div>
                    {logoDimensions?.width > 0 && logoDimensions?.height > 0 && (
                        <span className="clinic-info-logo-card__meta">
                            {logoDimensions.width} × {logoDimensions.height}px
                        </span>
                    )}
                </>
            ) : (
                <div className="clinic-info-logo-card__empty">
                    <i className="bi bi-image"></i>
                    <span>No logo uploaded</span>
                </div>
            )}
        </div>
    </div>
);

export const ClinicLogoEditCard = ({ logo, logoDimensions, register, onLogoBlur }) => (
    <div className="clinic-info-logo-card card">
        <div className="clinic-info-logo-card__header">
            <div className="clinic-info-panel__heading">
                <span className="clinic-info-panel__icon">
                    <i className="bi bi-image"></i>
                </span>
                <h6 className="clinic-info-panel__title mb-0">Clinic Logo</h6>
            </div>
        </div>
        <div className="clinic-info-logo-card__body">
            {logo ? (
                <div className="clinic-info-logo-card__frame mb-3">
                    <img src={logo} alt="Clinic Logo" className="clinic-info-logo-card__image" />
                </div>
            ) : (
                <div className="clinic-info-logo-card__empty mb-3">
                    <i className="bi bi-image"></i>
                    <span>No logo preview</span>
                </div>
            )}
            {logoDimensions?.width > 0 && logoDimensions?.height > 0 && (
                <span className="clinic-info-logo-card__meta d-block mb-3">
                    {logoDimensions.width} × {logoDimensions.height}px
                </span>
            )}
            <label className="clinic-info-form-field__label" htmlFor="clinic-logo-url">
                Logo URL
            </label>
            <input
                id="clinic-logo-url"
                type="url"
                className="form-control form-control-sm"
                placeholder="https://example.com/logo.png"
                {...register('logo')}
                onBlur={onLogoBlur}
            />
        </div>
    </div>
);

export const VerificationSummaryCard = ({ verification }) => {
    const isFullyVerified =
        verification?.email && verification?.phone && verification?.sms;

    return (
        <div
            className={`clinic-info-verification-card ${
                isFullyVerified
                    ? 'clinic-info-verification-card--verified'
                    : 'clinic-info-verification-card--pending'
            }`}
        >
            <span className="clinic-info-verification-card__icon">
                <i
                    className={`bi ${
                        isFullyVerified ? 'bi-shield-check' : 'bi-shield-exclamation'
                    }`}
                ></i>
            </span>
            <div>
                <strong className="clinic-info-verification-card__title">
                    {isFullyVerified ? 'All Channels Verified' : 'Verification Required'}
                </strong>
                <p className="clinic-info-verification-card__text mb-0">
                    {isFullyVerified
                        ? 'Email, phone, and SMS are verified for this clinic.'
                        : 'Verify email, phone, and SMS to ensure accurate patient communication.'}
                </p>
                {!verification?.email && (
                    <Link to="/settings/verify-email" className="btn btn-outline-primary btn-sm mt-2">
                        <i className="bi bi-envelope-check me-1"></i>
                        Verify Email
                    </Link>
                )}
            </div>
        </div>
    );
};

export const ClinicTimezoneCard = ({ timezoneName }) => (
    <div className="clinic-info-timezone-card card">
        <div className="clinic-info-timezone-card__header">
            <div className="clinic-info-panel__heading">
                <span className="clinic-info-panel__icon">
                    <i className="bi bi-clock"></i>
                </span>
                <h6 className="clinic-info-panel__title mb-0">Timezone</h6>
            </div>
        </div>
        <div className="clinic-info-timezone-card__value">
            {timezoneName || '—'}
        </div>
    </div>
);

export const ClinicInfoFormField = ({
    label,
    error,
    children,
    className = '',
    hint,
    htmlFor,
}) => (
    <div className={`clinic-info-form-field ${className}`.trim()}>
        <label className="clinic-info-form-field__label" htmlFor={htmlFor}>
            {label}
        </label>
        <div className="clinic-info-form-field__control">{children}</div>
        {hint && !error && <div className="clinic-info-form-field__hint">{hint}</div>}
        {error && <div className="clinic-info-form-field__error">{error}</div>}
    </div>
);
